import * as t from 'io-ts';

import {BugsnagSeveritiesEnum} from '../../../libs/shared/helpers/src/bugsnag/bugsnagSeverities';
import {ErrorWithData} from '../../../libs/shared/helpers/src/types/customErrors';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {makeDefaultValueType, makeEnumRuntimeType} from './apiTypes';
import {ChannelTypesEnum} from './channelCommonTypes';
import {ContactSourcesEnum, RContactSource} from './contactModel';
import {MessageTypesEnum} from './messageModel';

export enum ChannelExternalTypesEnum {
  DRIFT = 'drift',
  DIALPAD = 'dialpad',
  JUSTCALL = 'justcall',
  YALO = 'yalo',
  LIVEWEB = 'liveweb',
  MEYA = 'meya',
  INSTAGRAM = 'instagram',
  SENDBEE_WHATSAPP = 'sendbee_whatsapp',
  SLACK = 'slack',
  FACEBOOK = 'facebook',
}

export enum ChannelContactTypesEnum {
  CUSTOM = 'custom',
  EMAIL = 'email',
  PHONE = 'phone',
}

export enum ChannelComposeModesEnum {
  NORMAL = 'normal',
  UNSUPPORTED = 'unsupported',
}
const RChannelComposeMode = makeEnumRuntimeType(ChannelComposeModesEnum);

export enum ChannelReplyModesEnum {
  NORMAL = 'normal',
  SAME_CHANNEL = 'same_channel',
  NO_CONSECUTIVE = 'no_consecutive',
  UNSUPPORTED = 'unsupported',
}
const RChannelReplyMode = makeEnumRuntimeType(ChannelReplyModesEnum);

export enum ChannelComposerTypesEnum {
  BASIC = 'basic',
  ADVANCED = 'advanced',
}
const RChannelComposerType = makeEnumRuntimeType(ChannelComposerTypesEnum);

const RChannelAttachmentsBehavior = t.readonly(
  t.type({
    maxCount: t.union([t.undefined, t.Integer]),
    maxTotalSize: t.union([t.undefined, t.Integer]),
    types: t.union([t.undefined, t.readonlyArray(t.string)]),
  }),
);

const RChannelBehaviorsDirty = t.readonly(
  t.type({
    composePriority: makeDefaultValueType(t.Integer, 0),
    composeMode: makeDefaultValueType(RChannelComposeMode, ChannelComposeModesEnum.NORMAL),
    replyMode: makeDefaultValueType(RChannelReplyMode, ChannelReplyModesEnum.NORMAL),
    supportsSignatures: makeDefaultValueType(t.boolean, false),
    messageLengthLimit: makeDefaultValueType(t.union([t.undefined, t.Integer]), undefined),
    softMessageLengthLimit: makeDefaultValueType(t.union([t.undefined, t.Integer]), undefined),
    attachments: makeDefaultValueType(t.union([t.undefined, RChannelAttachmentsBehavior]), undefined),
    alwaysShowReplyComposer: makeDefaultValueType(t.boolean, false),
    canReplyToCalendarInvite: makeDefaultValueType(t.boolean, false),
    canReplyToDeclinedCalendarInvite: makeDefaultValueType(t.boolean, true),
    supportedContactSources: makeDefaultValueType(t.readonlyArray(RContactSource), []),
    isMissingCalendarScopes: makeDefaultValueType(t.boolean, false),
    shouldUpdateSubjectOnReply: makeDefaultValueType(t.boolean, false),
    canReplyToCalendarInviteWithComment: makeDefaultValueType(t.boolean, false),
    composerType: makeDefaultValueType(RChannelComposerType, ChannelComposerTypesEnum.BASIC),
    hasSpfDkimWarning: makeDefaultValueType(t.boolean, false),
  }),
);

/*
 * Type.
 */

export interface ChannelAttachmentsBehaviorModel extends t.TypeOf<typeof RChannelAttachmentsBehavior> {}
export interface ChannelBehaviorsModel extends t.TypeOf<typeof RChannelBehaviorsDirty> {}
export const RChannelBehaviors = t.clean<ChannelBehaviorsModel>(RChannelBehaviorsDirty as any);

/*
 * IO methods.
 */

const alwaysShowReplyComposerChannelTypes = [
  ChannelTypesEnum.INTERCOM,
  ChannelTypesEnum.FACEBOOK,
  ChannelTypesEnum.LAYER_ANON,
  ChannelTypesEnum.SMOOCH,
  ChannelTypesEnum.YALO_WHATSAPP,
  ChannelTypesEnum.FRONT_CHAT,
  ChannelTypesEnum.CUSTOM,
  ChannelTypesEnum.TWITTER_DM,
];
const canReplyToCalendarInviteChannelTypes = [
  ChannelTypesEnum.FRONT_MAIL,
  ChannelTypesEnum.GMAIL,
  ChannelTypesEnum.OFFICE365,
  ChannelTypesEnum.SMTP,
];
const canReplyToCalendarInviteWithCommentChannelTypes = [ChannelTypesEnum.GMAIL, ChannelTypesEnum.OFFICE365];

export function parseChannelBehaviors(src: any): ChannelBehaviorsModel {
  if (!src.properties) {
    return {} as ChannelBehaviorsModel;
  }

  if (!src.properties.composerType) {
    reportToBugsnag(
      new ErrorWithData('Composer type property missing for channel', {
        channelId: src.id,
        composerTypeSetting: src.settings.composerType,
      }),
      {
        severity: BugsnagSeveritiesEnum.WARNING,
      },
    );
  }

  return {
    composePriority: src.properties.composePriority,
    composeMode: src.properties.composeMode,
    replyMode: src.properties.replyMode,
    supportsSignatures: src.properties.supportsSignature,
    messageLengthLimit: src.properties.maxCharacterCount || undefined,
    softMessageLengthLimit: parseSoftMessagelengthLimit(src),
    attachments: parseAttachmentsBehavior(src),
    alwaysShowReplyComposer: parseAlwaysShowReplyComposer(src.type),
    canReplyToCalendarInvite: parseCanReplyToCalendarInvite(src.type),
    canReplyToDeclinedCalendarInvite: parseCanReplyToDeclinedCalendarInvite(src.type),
    supportedContactSources: parseSupportedContactSources(src.message_type),
    isMissingCalendarScopes: Boolean(src.is_missing_calendar_scopes),
    shouldUpdateSubjectOnReply: parseShouldUpdateSubjectOnReply(src),
    canReplyToCalendarInviteWithComment: parseCanReplyToCalendarInviteWithComment(src.type),
    composerType: src.properties.composerType || ChannelComposerTypesEnum.BASIC,
    hasSpfDkimWarning: src.spf_dkim_warning ?? false,
  };
}

type SupportedContactSources = {[source: string]: ReadonlyArray<ContactSourcesEnum>};
const supportedContactSources: SupportedContactSources = {
  [MessageTypesEnum.EMAIL]: [ContactSourcesEnum.EMAIL],
  [MessageTypesEnum.CALL]: [ContactSourcesEnum.PHONE],
  [MessageTypesEnum.TWEET]: [ContactSourcesEnum.TWITTER, ContactSourcesEnum.TWITTER_DM],
  [MessageTypesEnum.TWEET_DM]: [ContactSourcesEnum.TWITTER, ContactSourcesEnum.TWITTER_DM],
  [MessageTypesEnum.SMS]: [ContactSourcesEnum.PHONE],
  [MessageTypesEnum.YALO_WHATSAPP]: [ContactSourcesEnum.PHONE],
  [MessageTypesEnum.WHATSAPP]: [ContactSourcesEnum.PHONE],
  [MessageTypesEnum.FACEBOOK]: [ContactSourcesEnum.FACEBOOK],
  [MessageTypesEnum.INTERCOM]: [ContactSourcesEnum.INTERCOM],
  [MessageTypesEnum.GOOGLE_PLAY]: [ContactSourcesEnum.GOOGLE_PLAY],
};

function parseSoftMessagelengthLimit(src: any) {
  if (src.message_type !== MessageTypesEnum.SMS) {
    return undefined;
  }

  // TODO: Add to the backend channel behaviors and remove default value.
  return (src && src.properties && src.properties.softMessageLengthLimit) || 160;
}

function parseAttachmentsBehavior(src: any): ChannelAttachmentsBehaviorModel | undefined {
  const {properties} = src;
  if (!properties.supportsAttachments) {
    return undefined;
  }

  return {
    maxCount: properties.maxAttachmentsCount || undefined,
    maxTotalSize: (src.settings && src.settings.attachmentSizeLimit) || undefined,
    types: properties.supportedAttachmentTypes || undefined,
  };
}

// Update message subject only for emails and non-external channels.
function parseShouldUpdateSubjectOnReply(src: any): boolean {
  const isEmail = src.message_type === MessageTypesEnum.EMAIL;
  const isCustom = src.message_type === MessageTypesEnum.CUSTOM;
  return isEmail || Boolean(isCustom && !src.settings.externalChannelTypeId);
}

export function parseCanReplyToCalendarInvite(type: any) {
  return canReplyToCalendarInviteChannelTypes.includes(type);
}

export function parseCanReplyToDeclinedCalendarInvite(type: any) {
  return type !== ChannelTypesEnum.OFFICE365;
}

export function parseAlwaysShowReplyComposer(type: any) {
  return alwaysShowReplyComposerChannelTypes.includes(type);
}

export function parseSupportedContactSources(messageType: any) {
  return supportedContactSources[messageType] || [];
}

export function parseCanReplyToCalendarInviteWithComment(type: any) {
  return canReplyToCalendarInviteWithCommentChannelTypes.includes(type);
}
